import { Directive, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';

type ControlValidation = {
  hasError: boolean;
  invalid: boolean;
  dirty: boolean;
  message: string;
  errors: string[];
};

type Validation<T> = {
  [K in keyof T]: T[K] extends FormControl<any>
    ? ControlValidation
    : T[K] extends FormGroup<any>
      ? Validation<T[K]['controls']>
      : T[K] extends FormArray<FormGroup<any>>
        ? Array<Validation<T[K]['controls'][number]['controls']>>
        : never;
};

@Directive()
export class Crud<TCrud extends { [K in keyof TCrud]: AbstractControl<any, any>; }> implements OnInit, OnDestroy {

  @Input()
  public id: number | null = null;

  @Input()
  public isModal: boolean = false;

  public isUpdate = !!this.id;

  public validation!: Validation<TCrud>;

  private readonly location: Location = inject(Location);

  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);

  private readonly mapErrors: { [key: string]: string } = {
    required: 'Campo {field} é obrigatório',
    min: 'O campo {field} deve ser maior que {min}',
    max: 'O campo {field} deve ser menor que {max}',
    minlength: 'O campo {field} deve ter pelo menos {minlength} caracteres',
    maxlength: 'O campo {field} deve ter no maximo {maxlength} caracteres',
    pattern: 'O campo {field} é inválido',
  };
  
  private readonly subscriptions: Subscription[] = [];

  protected form = new FormGroup<TCrud>(<TCrud>{});

  protected globalError: string = '';

  protected mapField?: { [K in keyof TCrud]?: string }
  
  public async ngOnInit(): Promise<void> {
    this.defineErrors();

    if (!this.id) {
      const id = this.activatedRoute.snapshot.paramMap.get('id');
      this.id = id ? +id : null;
    }

    this.isUpdate = !!this.id;
    await this.initialize();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  public return(): void {
    this.location.back();
  };

  /**
    Use this method when you update your form structure.
  **/
  public tap(): void {
    this.defineErrors();
  }

  private defineErrors(): void {
    this.globalError = '';
    this.subscriptions.forEach(s => s.unsubscribe());

    this.validation = this.processFormGroup(this.form);
  }

  private processAbstractControl(key: string, control: AbstractControl<any, any>): any {
    let validation;

    if (control instanceof FormGroup)
      validation = this.processFormGroup(control);

    if (control instanceof FormArray)
      validation = this.processFormArray(control);

    if (control instanceof FormControl)
      validation = this.processFormControl(key, control);

    return validation;
  }

  private processFormGroup(form: FormGroup): any {
    let mainValidation = {} as any;

    for (const [key, control] of Object.entries(form.controls)) {
      let validation;

      if (control instanceof FormGroup)
        validation = this.processFormGroup(control);

      if (control instanceof FormArray)
        validation = this.processFormArray(control);

      if (control instanceof FormControl)
        validation = this.processFormControl(key, control);

      mainValidation = {
        ...mainValidation,
        [key]: validation,
      }
    }

    return mainValidation;
  }

  private processFormArray(array: FormArray): any[] {
    const mainValidation: any[] = [];

    for (const [key, control] of Object.entries(array.controls)) {
      let validation;

      if (control instanceof FormGroup)
        validation = this.processFormGroup(control);

      if (control instanceof FormArray)
        validation = this.processFormArray(control);

      if (control instanceof FormControl)
        validation = this.processFormControl(key, control);

      mainValidation.push(validation);
    }

    return mainValidation;
  }

  private processFormControl(key: string, abstractControl: AbstractControl): any {
    const errors = Object.keys(abstractControl.errors || {});
    const message = errors.length ? this.extractError(errors[0], key as keyof TCrud) : '';

    this.globalError = message;
    const validation = {
      hasError: abstractControl.invalid && abstractControl.dirty,
      invalid: abstractControl.invalid,
      dirty: abstractControl.dirty,
      message,
      errors: errors.map(e => this.extractError(e, key as keyof TCrud))
    };

    const subscription = abstractControl.valueChanges.subscribe(() => {
      const errors = Object.keys(abstractControl.errors || {});
      const message = errors.length ? this.extractError(errors[0], key as keyof TCrud) : '';

      validation.hasError = abstractControl.invalid && abstractControl.dirty;
      validation.invalid = abstractControl.invalid;
      validation.dirty = abstractControl.dirty;
      validation.message = message;
      validation.errors = errors.map(e => this.extractError(e, key as keyof TCrud));
    })

    this.subscriptions.push(subscription);

    return validation;
  }

  private extractError(key: string, field: keyof TCrud): string {
    const error = this.mapErrors[key];

    if (!error)
      return '';

    const fieldName = this.mapField?.[field] ?? (field as string) ?? '';

    return error.replace('{field} ', fieldName ? fieldName + ' ' : '');
  }

  protected async initialize(): Promise<void> {
    return;
  }

}
